/**
 * @fileoverview State for creating a user on the full page or in the dialog.
 * @module components/userCreation/useUserCreationForm
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { createUser, getCreatableRoles } from "../../logic/user/index.js";
import { creatableRoleState } from "../../logic/user/creatableRoleSelection.js";
import { validateUserFormFields } from "../../utils/userFormValidation.js";
import { getApiErrorMessage } from "../../utils/apiErrorMessages.js";
import {
  validateEmail,
  validateMobilePhoneNumber,
  validateRFC,
} from "../../utils/common.js";
import { logger, sanitizeError } from "../../utils/logger.js";
import {
  SUCCESS_MESSAGE_TIMEOUT_MS,
  isAllowedAvatarFile,
  isUserCreationFormValid,
  rfcSourceForUser,
} from "./userCreationFormModel";

/**
 * @param {Object} input
 */
const resetUserCreationFields = ({ user, fileInputRef, setters }) => {
  setters.setUserType(!user?.esColaborador ? "3" : null);
  setters.setName("");
  setters.setTitle("");
  setters.setEmail("");
  setters.setNumeroCelular("");
  setters.setRFC(!user?.esColaborador ? user?.RFC : "");
  setters.setProfileImage(null);
  setters.setProfileImageFormat("");
  setters.setRFCError(false);
  setters.setEmailError(false);
  setters.setEmailFormatError(null);
  setters.setPhoneFormatError(null);
  setters.setRfcFormatError(null);
  setters.setCreateError(null);
  if (fileInputRef.current) {
    fileInputRef.current.value = "";
  }
};

/**
 * @param {Object} input
 * @returns {Promise<void>}
 */
const submitNewUser = async ({
  fields,
  user,
  roles,
  navigate,
  onAddUser,
  onFinished,
  setters,
}) => {
  const rfcValue = rfcSourceForUser(user, fields.RFC);
  const validation = validateUserFormFields({
    correoElectronico: fields.correoElectronico,
    numeroCelular: fields.numeroCelular,
    RFC: rfcValue,
  });
  setters.setEmailFormatError(validation.emailFormatError);
  setters.setPhoneFormatError(validation.phoneFormatError);
  setters.setRfcFormatError(validation.rfcFormatError);
  if (
    !validation.valid ||
    fields.correoElectronico.trim() === "" ||
    fields.tipoDeUsuario === null
  ) {
    return;
  }

  setters.setCreateError(null);
  setters.setLoading(true);
  const userData = {
    tipoDeUsuario: fields.tipoDeUsuario,
    nombre: fields.nombre,
    titulo: fields.titulo,
    correoElectronico: fields.correoElectronico,
    numeroCelular: fields.numeroCelular,
    RFC: fields.RFC,
    imagenDePerfil: fields.imagenDePerfil,
    imagenDePerfilFormato: fields.imagenDePerfilFormato,
    activo: true,
  };

  try {
    await createUser(userData, roles, {
      navigate,
      onSuccess: (newUser) => {
        onAddUser(newUser);
        onFinished();
        setters.setShowSuccessMessage(true);
        setTimeout(
          () => setters.setShowSuccessMessage(false),
          SUCCESS_MESSAGE_TIMEOUT_MS,
        );
      },
      onRFCError: () => setters.setRFCError(true),
      onEmailError: () => setters.setEmailError(true),
      onError: (errorData) => setters.setCreateError(getApiErrorMessage(errorData)),
    });
  } catch (error) {
    logger.error("Error:", { err: sanitizeError(error) });
  } finally {
    setters.setLoading(false);
  }
};

/**
 * @param {File} file
 * @param {Function} setProfileImage
 * @param {Function} setProfileImageFormat
 */
const readAvatarFile = (file, setProfileImage, setProfileImageFormat) => {
  if (!isAllowedAvatarFile(file)) {
    logger.error("Invalid file type. Please upload a JPEG, PNG, or WEBP image.");
    return;
  }
  const reader = new FileReader();
  reader.onload = (loadEvent) => {
    setProfileImage(loadEvent.target.result);
    setProfileImageFormat(file.type.split("/")[1]);
  };
  reader.readAsDataURL(file);
};

/**
 * Field, avatar, and submit handlers. Each one keeps the previous validation side effects.
 * @param {Object} input
 * @returns {Object}
 */
const useUserCreationInput = (input) => {
  const { setters } = input;
  const handleRoleChange = useCallback((selectedOption) => {
    if (selectedOption) {
      setters.setUserType(selectedOption.value);
    }
  }, [setters]);
  const handleNameChange = useCallback((event) => {
    setters.setName(event.target.value);
  }, [setters]);
  const handleTitleChange = useCallback((event) => {
    setters.setTitle(event.target.value);
  }, [setters]);
  const handleEmailChange = useCallback((event) => {
    setters.setEmail(event.target.value);
    setters.setEmailError(false);
    setters.setEmailFormatError(null);
  }, [setters]);
  const handlePhoneChange = useCallback((event) => {
    setters.setNumeroCelular(event.target.value);
    setters.setPhoneFormatError(null);
  }, [setters]);
  const handleRfcChange = useCallback((event) => {
    setters.setRFC(event.target.value);
    setters.setRfcFormatError(null);
  }, [setters]);
  const handleEmailBlur = useCallback(() => {
    if (input.correoElectronico.trim() === "") {
      setters.setEmailFormatError(null);
      return;
    }
    const result = validateEmail(input.correoElectronico);
    setters.setEmailFormatError(result.isValid ? null : result.error);
  }, [input.correoElectronico, setters]);
  const handlePhoneBlur = useCallback(() => {
    if (input.numeroCelular.trim() === "") {
      setters.setPhoneFormatError(null);
      return;
    }
    const result = validateMobilePhoneNumber(input.numeroCelular);
    setters.setPhoneFormatError(result.isValid ? null : result.error);
  }, [input.numeroCelular, setters]);
  const handleRfcBlur = useCallback(() => {
    const rfcValue = rfcSourceForUser(input.user, input.RFC);
    if (rfcValue.trim() === "") {
      setters.setRfcFormatError(null);
      return;
    }
    const result = validateRFC(rfcValue);
    setters.setRfcFormatError(result.isValid ? null : result.error);
  }, [input.RFC, input.user, setters]);
  const clearName = useCallback(() => setters.setName(""), [setters]);
  const clearTitle = useCallback(() => setters.setTitle(""), [setters]);
  const clearEmail = useCallback(() => {
    setters.setEmail("");
    setters.setEmailError(false);
  }, [setters]);
  const clearPhone = useCallback(() => setters.setNumeroCelular(""), [setters]);
  const clearRfc = useCallback(() => {
    setters.setRFC("");
    setters.setRFCError(false);
    setters.setRfcFormatError(null);
  }, [setters]);
  const handleDeleteAvatar = useCallback(() => {
    setters.setProfileImage(null);
    setters.setProfileImageFormat("");
    if (input.fileInputRef.current) {
      input.fileInputRef.current.value = "";
    }
  }, [input.fileInputRef, setters]);
  const handleAvatarEditClick = useCallback(() => {
    input.fileInputRef.current.click();
  }, [input.fileInputRef]);
  const handleAvatarChange = useCallback((event) => {
    readAvatarFile(
      event.target.files[0],
      setters.setProfileImage,
      setters.setProfileImageFormat,
    );
  }, [setters]);
  const handleCreateUser = useCallback(
    () =>
      submitNewUser({
        fields: {
          tipoDeUsuario: input.tipoDeUsuario,
          nombre: input.nombre,
          titulo: input.titulo,
          correoElectronico: input.correoElectronico,
          numeroCelular: input.numeroCelular,
          RFC: input.RFC,
          imagenDePerfil: input.imagenDePerfil,
          imagenDePerfilFormato: input.imagenDePerfilFormato,
        },
        user: input.user,
        roles: input.roles,
        navigate: input.navigate,
        onAddUser: input.onAddUser,
        onFinished: input.onFinished,
        setters,
      }),
    [input, setters],
  );

  return {
    handleRoleChange,
    handleNameChange,
    handleTitleChange,
    handleEmailChange,
    handleEmailBlur,
    handlePhoneChange,
    handlePhoneBlur,
    handleRfcChange,
    handleRfcBlur,
    clearName,
    clearTitle,
    clearEmail,
    clearPhone,
    clearRfc,
    handleDeleteAvatar,
    handleAvatarEditClick,
    handleAvatarChange,
    handleCreateUser,
  };
};

/**
 * Create-user form shared by the full page and the dialog.
 * The page stays active. The dialog resets only while it is open.
 * @param {Object} input
 * @param {Object} [input.user]
 * @param {boolean} input.active
 * @param {Function} input.onAddUser
 * @param {Function} input.onFinished
 * @returns {Object}
 */
export function useUserCreationForm({ user, active, onAddUser, onFinished }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [tipoDeUsuario, setUserType] = useState(null);
  const [roles, setRoles] = useState([]);
  const [nombre, setName] = useState("");
  const [titulo, setTitle] = useState("");
  const [correoElectronico, setEmail] = useState("");
  const [numeroCelular, setNumeroCelular] = useState("");
  const [RFC, setRFC] = useState("");
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [imagenDePerfil, setProfileImage] = useState(null);
  const [imagenDePerfilFormato, setProfileImageFormat] = useState("");
  const [loading, setLoading] = useState(false);
  const [RFCError, setRFCError] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [emailFormatError, setEmailFormatError] = useState(null);
  const [phoneFormatError, setPhoneFormatError] = useState(null);
  const [rfcFormatError, setRfcFormatError] = useState(null);
  const [createError, setCreateError] = useState(null);
  const fileInputRef = useRef(null);

  const setters = {
    setUserType,
    setName,
    setTitle,
    setEmail,
    setNumeroCelular,
    setRFC,
    setProfileImage,
    setProfileImageFormat,
    setRFCError,
    setEmailError,
    setEmailFormatError,
    setPhoneFormatError,
    setRfcFormatError,
    setCreateError,
    setLoading,
    setShowSuccessMessage,
  };

  useEffect(() => {
    if (!active) {
      return undefined;
    }
    let cancelled = false;
    resetUserCreationFields({ user, fileInputRef, setters });
    getCreatableRoles(navigate).then((list) => {
      if (cancelled) {
        return;
      }
      const next = creatableRoleState(list, Boolean(user?.esColaborador));
      setRoles(next.roles);
      setUserType(next.tipoDeUsuario);
      if (next.roles.length === 0) {
        setCreateError(t("errors.api.FORBIDDEN"));
      }
    });
    return () => {
      cancelled = true;
    };
    // Reset when the surface opens or the company context changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, navigate, t, user?.esColaborador, user?.RFC]);

  const input = useUserCreationInput({
    user,
    roles,
    navigate,
    onAddUser,
    onFinished,
    tipoDeUsuario,
    nombre,
    titulo,
    correoElectronico,
    numeroCelular,
    RFC,
    imagenDePerfil,
    imagenDePerfilFormato,
    fileInputRef,
    setters,
  });
  const rfcValue = rfcSourceForUser(user, RFC);

  return {
    navigate,
    t,
    tipoDeUsuario,
    roles,
    nombre,
    titulo,
    correoElectronico,
    numeroCelular,
    RFC,
    imagenDePerfil,
    loading,
    showSuccessMessage,
    createError,
    emailError,
    emailFormatError,
    phoneFormatError,
    RFCError,
    rfcFormatError,
    fileInputRef,
    userTypeLocked: roles.length === 1 || !user?.esColaborador,
    canSubmit: isUserCreationFormValid({
      correoElectronico,
      tipoDeUsuario,
      numeroCelular,
      rfcValue,
    }),
    ...input,
  };
}
